/**
 * Batch Card Component
 * Displays a single annotation batch with progress and actions
 */

import { AnnotationBatch, BatchStatus } from '@/types/annotation';
import { Button } from '@/components/ui/Button';
import { Play, Archive, Trash2, Edit, ExternalLink, RotateCcw } from 'lucide-react';
import { useI18n } from '@/i18n';

interface BatchCardProps {
  batch: AnnotationBatch;
  onActivate?: (batch: AnnotationBatch) => void;
  onArchive?: (batch: AnnotationBatch) => void;
  onRestore?: (batch: AnnotationBatch) => void;
  onDelete?: (batch: AnnotationBatch) => void;
  onEdit?: (batch: AnnotationBatch) => void;
  onViewTasks?: (batch: AnnotationBatch) => void;
}

const statusColors: Record<BatchStatus, { bg: string; text: string; border: string }> = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  active: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  completed: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  archived: { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200' },
};

export function BatchCard({
  batch,
  onActivate,
  onArchive,
  onRestore,
  onDelete,
  onEdit,
  onViewTasks,
}: BatchCardProps) {
  const { t } = useI18n();
  const colors = statusColors[batch.status as BatchStatus];
  const progress = batch.progress_percent;

  const statusLabels: Record<BatchStatus, string> = {
    draft: t('batchCard.status.draft'),
    active: t('batchCard.status.active'),
    completed: t('batchCard.status.completed'),
    archived: t('batchCard.status.archived'),
  };

  return (
    <div className={`bg-white rounded-lg border ${colors.border} shadow-sm hover:shadow-md transition-shadow`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {batch.title}
              </h3>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors.bg} ${colors.text}`}>
                {statusLabels[batch.status as BatchStatus]}
              </span>
            </div>
            <p className="text-sm text-gray-500 font-mono">{batch.batch_id}</p>
          </div>
        </div>

        {batch.description && (
          <p className="mt-2 text-sm text-gray-600 line-clamp-2">{batch.description}</p>
        )}
      </div>

      {/* Progress */}
      <div className="px-4 py-3 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">{t('batchCard.progress')}</span>
          <span className="text-sm font-medium text-gray-900">
            {batch.completed_tasks} / {batch.total_tasks} ({progress.toFixed(1)}%)
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              progress >= 100 ? 'bg-green-500' : progress > 50 ? 'bg-blue-500' : 'bg-amber-500'
            }`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 grid grid-cols-3 gap-4 text-center border-t border-gray-100">
        <div>
          <div className="text-lg font-semibold text-gray-900">{batch.total_tasks}</div>
          <div className="text-xs text-gray-500">{t('batchCard.stats.totalTasks')}</div>
        </div>
        <div>
          <div className="text-lg font-semibold text-green-600">{batch.completed_tasks}</div>
          <div className="text-xs text-gray-500">{t('batchCard.stats.completed')}</div>
        </div>
        <div>
          <div className="text-lg font-semibold text-amber-600">
            {batch.total_tasks - batch.completed_tasks}
          </div>
          <div className="text-xs text-gray-500">{t('batchCard.stats.pending')}</div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {batch.status === 'draft' && onActivate && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onActivate(batch)}
              disabled={batch.total_tasks === 0}
              title={batch.total_tasks === 0 ? t('batchCard.actions.noTasksTitle') : t('batchCard.actions.activateTitle')}
            >
              <Play className="w-4 h-4 mr-1" />
              {t('batchCard.actions.activate')}
            </Button>
          )}

          {batch.status === 'active' && onViewTasks && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => onViewTasks(batch)}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              {t('batchCard.actions.startAnnotation')}
            </Button>
          )}

          {(batch.status === 'active' || batch.status === 'completed') && onArchive && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onArchive(batch)}
            >
              <Archive className="w-4 h-4 mr-1" />
              {t('batchCard.actions.archive')}
            </Button>
          )}

          {batch.status === 'archived' && onRestore && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onRestore(batch)}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              {t('batchCard.actions.restore')}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {batch.status === 'draft' && onEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(batch)}
            >
              <Edit className="w-4 h-4" />
            </Button>
          )}

          {batch.status === 'draft' && onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(batch)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="px-4 py-2 bg-gray-50 rounded-b-lg text-xs text-gray-500 flex items-center justify-between">
        <span>{t('batchCard.metadata.source')}: {batch.source_type}</span>
        <span>{t('batchCard.metadata.created')}: {new Date(batch.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
}
